import { USE_RECORDING_V2 } from '@/app-shell/flags/capture-flags';
import { isChunkedLiveDictation } from '@/app-shell/flags/live-dictate-transcript-flag';
import { IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED } from '@/app-shell/flags/templates-flags';
import { useSplitScreen } from '@/app-shell/session-page/ScribeTabs/hooks/useSplitScreen';
import { readyToRequestMicPermissionsAtom } from '@/atoms/audioAtoms';
import useMutateCreateDocumentFromNote from '@/hooks/mutation/useMutateCreateDocumentFromNote';
import {
  invalidateLiveTranscript,
  setIsPollingChunks,
  setSessionChunkUploadProgress,
} from '@/hooks/query/useQueryLiveTranscriptBySessionId';
import { useQueryTemplateById } from '@/hooks/query/useQueryTemplateById';
import { useSessionRecording } from '@/lib/SessionRecordingProvider/useSessionRecording';
import {
  sessionPauseRecording,
  sessionResumeRecording,
  sessionStartRecording,
  sessionStopRecording,
} from '@/lib/analytics/scribe/session';
import {
  cancelRecordingControlAttempt,
  finishRecordingControlAttempt,
  type RecordingControlOptions,
  type RecordingControlTracking,
} from '@/lib/telemetry/recordingControlTracking';
import {
  appendRecordingAttemptCheckpoint,
  bindEngineRecordingAttempt,
  finishRecordingAttempt,
  type RecordingAttemptTracking,
  type RecordingStartOptions,
} from '@/lib/telemetry/recordingStartTracking';
import { createSessionFlowPreflightError } from '@/lib/telemetry/sessionFlowFailures';
import { type Dispatch, store } from '@/store';
import { isOnPrem } from '@/utils/buildProfile';
import { logger } from '@/utils/logger/logger';
import { reactQueryClient } from '@/utils/reactQueryClient';
import { sanitizeError } from '@heidi/runtime/logger';
import { getActiveTrackings, TrackingKey } from '@heidi/runtime/performance';
import { useCurrentSessionId } from '@heidi/runtime/shell';
import {
  GenerationMethod,
  RecordingStatus,
  TemplateCategory,
} from '@heidi/schema/ml-scribe';
import {
  GET_SESSION_BY_ID,
  type QuerySessionResponse,
  TabType,
} from '@heidi/session/contracts';
import {
  activeSessionIdAtom,
  addStartNowStoppedSessionId,
  isTranscribeLikeMode,
  removeStartNowStoppedSessionId,
  startNowStoppedSessionIdsAtom,
  useCurrentSessionIsTabSharing,
  useMutateUpdateSessionRecordingStatus,
  useQuerySessionById,
  useSessionWindows,
} from '@heidi/session/data';
import { isGenerateConsultNoteFromDocumentTemplateEnabled } from '@heidi/session/flags';

import { isAxiosError } from 'axios';
import { useSetAtom, useStore } from 'jotai';
import { useCallback, useMemo } from 'react';
import { useDispatch } from 'react-redux';
import { useMutateUpdateSessionDuration } from '../mutation/useMutateUpdateSessionDuration';

import { useCheckIsBlockedOnConsent } from '@heidi/session';
import {
  claimRecordingStartEffects,
  ownsRecordingStartEffects,
  retireRecordingStartEffects,
} from './recording-start-lease';
import { recordingStartFailureForTelemetry } from './recording-start-outcome';
import { runRecordingStart } from './recording-start.machine';
import { useTutorialActions } from './useTutorialActions';

type StartSideEffect =
  | 'audio_checkpoint'
  | 'timer_checkpoint'
  | 'split_screen'
  | 'attempt_reporting'
  | 'analytics'
  | 'transcript_invalidation'
  | 'timer_cleanup'
  | 'duration_cache'
  | 'idle_status'
  | 'active_session_clear'
  | 'active_session_claim'
  | 'cleanup_confirmation'
  | 'failure_diagnostic';

function reportStartSideEffectFailure(step: StartSideEffect): void {
  try {
    logger.error({ event_type: 'START_SIDE_EFFECT_ERROR_V2', details: { step } });
  } catch {
    // A failing diagnostic transport cannot change recording protection.
  }
}

// Cleanup commits synchronously, without yielding between shared-state writes.
// Also observe a runtime promise from a declared-void Rematch effect.
function runSynchronousStartEffect(
  step: StartSideEffect,
  action: () => void | Promise<void>
): void {
  try {
    void Promise.resolve(action()).catch(() => reportStartSideEffectFailure(step));
  } catch {
    reportStartSideEffectFailure(step);
  }
}

// Ancillary failures must never become capture failures or skip compensation.
async function runStartSideEffect(
  step: StartSideEffect,
  action: () => void | Promise<void>
): Promise<void> {
  try {
    await action();
  } catch {
    reportStartSideEffectFailure(step);
  }
}

type StartRecording = (tracking?: RecordingAttemptTracking) => Promise<void>;

export const useRecordingV2 = () => {
  const sessionId = useCurrentSessionId();
  const { start, stop, isCaptureStopped } = useSessionRecording();
  const dispatch = useDispatch<Dispatch>();
  const checkIsBlockedOnConsent = useCheckIsBlockedOnConsent(sessionId);
  const { mutate: updateRecordingStatus } = useMutateUpdateSessionRecordingStatus();
  const tutorial = useTutorialActions();
  const { splitScreen } = useSplitScreen();
  const { ensureTabFocus } = useSessionWindows({ sessionId });
  const setReadyToRequestMicPerms = useSetAtom(readyToRequestMicPermissionsAtom);
  const setActiveSessionId = useSetAtom(activeSessionIdAtom);
  const atomStore = useStore();
  const setStoppedSessionIds = useSetAtom(startNowStoppedSessionIdsAtom);
  const isTabSharingSession = useCurrentSessionIsTabSharing();

  const { data: session } = useQuerySessionById({
    sessionId,
    select: (data) => {
      if (!data) return undefined;
      return {
        consult_note: data.consult_note,
      };
    },
  });
  const { data: template } = useQueryTemplateById(
    session?.consult_note.template_id,
    session?.consult_note.generation_method
  );
  const { mutate: createEmptyDocumentFromNote } = useMutateCreateDocumentFromNote({
    sessionId,
  });
  const { mutate: updateSessionDuration } = useMutateUpdateSessionDuration();

  const freezeTimerAndSeedDuration = useCallback(() => {
    const duration = Number(store.getState().timerModel.seconds);

    // `useUpdateTimerFromSession` makes cached session duration authoritative as
    // soon as the timer stops running. Seed that cache first so the transition
    // from live ticks to persisted duration is atomic and cannot briefly restore
    // zero (first stop) or the previous pause duration (resume then stop).
    reactQueryClient.setQueryData<QuerySessionResponse>(
      [GET_SESSION_BY_ID, { sessionId }],
      (old) => (old ? { ...old, duration } : old)
    );
    dispatch.timerModel.onPause();
    // A pause froze the clock at the press, leaving no interval for `onPause`
    // to clear and `isRunning` still true, so clear it here.
    dispatch.timerModel.setRunning(false);

    return duration;
  }, [dispatch.timerModel, sessionId]);

  const startRecording: StartRecording = useCallback(
    async (requestedTracking) => {
      // Claim synchronously before any await, across all instances of this hook.
      const lease = claimRecordingStartEffects(sessionId);
      const ownsStartup = () =>
        ownsRecordingStartEffects(lease) &&
        atomStore.get(activeSessionIdAtom) === sessionId;
      const cancelled = () =>
        createSessionFlowPreflightError(
          'StartRecordingCancelled',
          'Recording start superseded'
        );
      const commitCleanupEffect = (
        step: StartSideEffect,
        action: () => void | Promise<void>,
        requireStopped: boolean
      ) => {
        // Recheck before EACH write, including synchronous subscriber reentry.
        if (
          !ownsRecordingStartEffects(lease) ||
          atomStore.get(activeSessionIdAtom) !== sessionId
        )
          return;
        if (requireStopped) {
          try {
            if (!isCaptureStopped()) {
              reportStartSideEffectFailure('cleanup_confirmation');
              return;
            }
          } catch {
            reportStartSideEffectFailure('cleanup_confirmation');
            return;
          }
        }
        runSynchronousStartEffect(step, action);
      };
      const session = reactQueryClient.getQueryData<QuerySessionResponse>([
        GET_SESSION_BY_ID,
        { sessionId: sessionId },
      ]);

      // Determine resume-vs-start BEFORE start() — once start() runs and the
      // timer ticks, `seconds` would also be > 0 on a fresh start.
      const priorSeconds = store.getState().timerModel.seconds;
      const isResume = priorSeconds > 0;
      const fallbackTrackingKey = isResume
        ? TrackingKey.SCRIBE_RESUME_RECORDING
        : TrackingKey.SCRIBE_START_RECORDING;
      // Capture behaviour still comes from recorder/timer state. Telemetry uses
      // the click-time operation and instance so an async state change cannot
      // close a different operation or a newer retry.
      const tracking =
        requestedTracking ??
        ({
          trackingKey: fallbackTrackingKey,
          trackingInstanceId: getActiveTrackings().get(fallbackTrackingKey)?.instanceId,
        } satisfies RecordingAttemptTracking);

      if (!session?.transcribe_mode) {
        // A missing mode is a failed client attempt before capture starts.
        await runStartSideEffect('attempt_reporting', () =>
          finishRecordingAttempt(tracking, {
            outcome: 'failed',
            error: createSessionFlowPreflightError(
              'StartRecordingFailed',
              'Session has no transcription mode'
            ),
          })
        );
        // The button set the atom optimistically before preflight.
        commitCleanupEffect(
          'active_session_clear',
          () => setActiveSessionId(null),
          false
        );
        return;
      }

      // Direct Resume callers do not set the button's optimistic atom. Claim
      // protection before capture starts so their failure cleanup has an owner.
      // Never reclaim it after an ordinary Pause/Stop has revoked the lease.
      if (
        ownsRecordingStartEffects(lease) &&
        atomStore.get(activeSessionIdAtom) === null
      ) {
        runSynchronousStartEffect('active_session_claim', () =>
          setActiveSessionId(sessionId)
        );
      }

      const outcome = await runRecordingStart({
        startCapture: async () => {
          if (!ownsStartup()) throw cancelled();
          const unbindEngineAttempt = bindEngineRecordingAttempt(tracking);
          try {
            await start(isTabSharingSession);
          } finally {
            unbindEngineAttempt();
          }
          if (!ownsStartup()) throw cancelled();
          await runStartSideEffect('audio_checkpoint', () =>
            appendRecordingAttemptCheckpoint(tracking, 'audio_started')
          );
        },
        startTimer: async () => {
          // Explicit cancellation is an ownership decision, not a classification
          // of a timer exception. EVERY thrown timer error needs compensation.
          if (!ownsStartup()) return 'cancelled';
          // Rematch's declared effect return is void, but the runtime can
          // return a promise. Observe both synchronous and asynchronous failure.
          await Promise.resolve(dispatch.timerModel.onStart(session.duration ?? 0));
          await runStartSideEffect('timer_checkpoint', () =>
            appendRecordingAttemptCheckpoint(tracking, 'timer_started')
          );
          return 'started';
        },
        stopCapture: () => {
          // A superseded attempt must not enqueue teardown of its replacement.
          if (!ownsStartup()) return Promise.reject(cancelled());
          return stop('start-compensation');
        },
        isCaptureStopped,
      });

      await runStartSideEffect('attempt_reporting', () => {
        if (outcome.type === 'started') {
          finishRecordingAttempt(tracking);
        } else {
          // finishTracking classifies cancellation into a cancelled RUM action;
          // cancelTracking would silently discard it. Capture may have already
          // terminalized this exact attempt.
          finishRecordingAttempt(tracking, {
            outcome: 'failed',
            error: recordingStartFailureForTelemetry(outcome),
          });
        }
      });

      if (outcome.type !== 'started') {
        const audioStarted =
          outcome.type !== 'capture-failed' && outcome.type !== 'cancelled';
        await runStartSideEffect('failure_diagnostic', () =>
          logger.error({
            event_type: 'START_ERROR_V2',
            details: { outcome, audioStarted },
          })
        );
        if (outcome.type === 'setup-failed-stop-unconfirmed') {
          // Preserve navigation/session protection: capture may still be live.
          return;
        }
        if (outcome.type === 'setup-failed-capture-stopped') {
          // Timer.start may have partially overwritten the slice before throwing.
          // Restore the pre-start/session duration, never destructively reset it.
          const duration = Math.max(priorSeconds, session.duration ?? 0);
          commitCleanupEffect(
            'duration_cache',
            () => {
              reactQueryClient.setQueryData<QuerySessionResponse>(
                [GET_SESSION_BY_ID, { sessionId }],
                (old) => (old ? { ...old, duration } : old)
              );
            },
            true
          );
          commitCleanupEffect(
            'timer_cleanup',
            () => dispatch.timerModel.onStopWithDuration(duration),
            true
          );
        }
        commitCleanupEffect(
          'idle_status',
          () =>
            updateRecordingStatus({ sessionId, recordingStatus: RecordingStatus.IDLE }),
          audioStarted
        );
        commitCleanupEffect(
          'active_session_clear',
          () => setActiveSessionId(null),
          audioStarted
        );
        return;
      }

      // Protection was claimed before capture. Completion must never re-arm
      // a signal cleared by ordinary Pause/Stop while this start was pending.
      if (!ownsStartup()) return;
      // Capture + essential timer are already successful. Layout and analytics
      // are independent, nonfatal tail work, never reasons to reset live capture.
      await runStartSideEffect('split_screen', () => {
        if (ownsStartup())
          return splitScreen({
            isTranscriptTabPending: isChunkedLiveDictation(session.transcribe_mode),
          });
      });
      await runStartSideEffect('analytics', () => {
        if (!ownsStartup()) return;
        if (isResume) {
          // SCR-3481: refetch uploaded transcript without gating resume analytics.
          void runStartSideEffect('transcript_invalidation', () =>
            invalidateLiveTranscript(sessionId)
          );
          return sessionResumeRecording({
            sessionId,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });
        } else {
          const selectedDevice = store.getState().audio.selectedAudioSource;
          return sessionStartRecording({
            sessionId,
            transcribeMode: session.transcribe_mode,
            device: selectedDevice?.label,
            deviceId: selectedDevice?.deviceId,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });
        }
      });
    },
    [
      sessionId,
      start,
      stop,
      isCaptureStopped,
      isTabSharingSession,
      splitScreen,
      dispatch,
      updateRecordingStatus,
      setActiveSessionId,
      atomStore,
    ]
  );

  const onStartButtonPressed = useCallback(
    (props?: RecordingStartOptions) => {
      setReadyToRequestMicPerms(true);
      if (!props?.shouldBypassConsentCheck && checkIsBlockedOnConsent()) return;
      tutorial.onStart();
      void startRecording(props?.tracking);
    },
    [setReadyToRequestMicPerms, checkIsBlockedOnConsent, tutorial, startRecording]
  );

  const runStop = useCallback(
    async (isPaused: boolean, tracking?: RecordingControlTracking): Promise<void> => {
      retireRecordingStartEffects(sessionId);
      if (!sessionId) {
        // No session: drop the attempt.
        cancelRecordingControlAttempt(tracking);
        return;
      }

      if (!isPaused) {
        setStoppedSessionIds((current) =>
          addStartNowStoppedSessionId(current, sessionId)
        );
      }

      // Stop the clock at the Pause press: the header already shows PAUSED and
      // a native stop can take seconds. `isRunning` stays true until the audio
      // stops, then `freezeTimerAndSeedDuration` clears it and saves the value.
      if (isPaused) dispatch.timerModel.onFreeze();

      let audioStopped = false;
      try {
        const session = reactQueryClient.getQueryData<QuerySessionResponse>([
          GET_SESSION_BY_ID,
          { sessionId: sessionId },
        ]);
        await stop();
        audioStopped = true;
        // The attempt ends at capture stop; the work below is not part of it.
        if (isPaused) {
          finishRecordingControlAttempt(tracking);
        } else {
          const recordedSeconds = Math.round(
            Number(store.getState().timerModel.seconds) || 0
          );
          finishRecordingControlAttempt(
            tracking,
            recordedSeconds > 0
              ? { metadata: { recordingDurationS: recordedSeconds } }
              : {}
          );
        }
        if (!isPaused) {
          // List Stop follows this path via pendingRecordingAction, not the
          // header button. Clear the live-recording atom so the list does not
          // keep showing Stop after capture has already ended.
          setActiveSessionId(null);
        }
        void setIsPollingChunks(sessionId);
        // On-prem: seed session chunkUploadProgress before pausing the timer so
        // live transcript status/Loading share one source of truth while the
        // final chunk drains (note-gen will overwrite/advance this next).
        if (isOnPrem) {
          setSessionChunkUploadProgress(sessionId, 0);
        }
        // Only ending a recording should switch to Note while it generates.
        // A pause must preserve the clinician's current tab in split view.
        if (!isPaused) {
          ensureTabFocus(TabType.NOTE);
        }
        const duration = freezeTimerAndSeedDuration();
        tutorial.onStop();

        if (isPaused) {
          sessionPauseRecording({
            sessionId: sessionId,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });
        }

        if (!isPaused) {
          sessionStopRecording({
            sessionId: sessionId,
            duration,
            isRecordingV2Enabled: USE_RECORDING_V2(),
            isTabSharing: isTabSharingSession,
          });

          const shouldUseDocumentFlow =
            isTranscribeLikeMode(session?.transcribe_mode) &&
            ((!isGenerateConsultNoteFromDocumentTemplateEnabled() &&
              template?.template_category === TemplateCategory.DOCUMENT_TEMPLATE) ||
              (IS_RELEASE_FORM_FILLING_FLOW_ALIGNMENT_ENABLED() &&
                template?.template_category === TemplateCategory.FORM_TEMPLATE));

          if (shouldUseDocumentFlow) {
            createEmptyDocumentFromNote({
              trigger: 'autonomous',
              generationMethod: GenerationMethod.TEMPLATE,
              templateCategory: template.template_category,
              templateId: template.id,
            });
          } else {
            dispatch.scribeRecordModel._actionPostRecording({ sessionID: sessionId });
          }
        }

        updateSessionDuration(
          { sessionId, duration },
          {
            onError: () => {
              if (!isPaused) {
                setStoppedSessionIds((current) =>
                  removeStartNowStoppedSessionId(current, sessionId)
                );
              }
            },
          }
        );
      } catch (error) {
        // Only a throw before audio stopped fails the attempt.
        if (!audioStopped) {
          finishRecordingControlAttempt(tracking, { outcome: 'failed', error });
          // Capture is still running, so let the clock run again. It counts
          // from the recording's start instant, so no seconds are lost.
          if (isPaused) dispatch.timerModel.onResumeFrozen();
        }
        if (!isPaused) {
          setStoppedSessionIds((current) =>
            removeStartNowStoppedSessionId(current, sessionId)
          );
        }
        logger.error({
          event_type: 'STOP_ERROR_V2',
          details: {
            errorName: error instanceof Error ? error.constructor.name : typeof error,
            // An axios rejection's `message` can embed the request URL/body.
            ...(isAxiosError(error) ? sanitizeError(error) : {}),
          },
        });
      }
    },
    [
      sessionId,
      stop,
      ensureTabFocus,
      dispatch,
      freezeTimerAndSeedDuration,
      tutorial,
      updateSessionDuration,
      isTabSharingSession,
      template,
      createEmptyDocumentFromNote,
      setStoppedSessionIds,
      setActiveSessionId,
    ]
  );
  const onStopButtonPressed = useCallback(
    (isPaused: boolean, tracking?: RecordingControlTracking): void => {
      void runStop(isPaused, tracking);
    },
    [runStop]
  );

  const onInterrupt = useCallback(() => {
    if (!sessionId) return;
    retireRecordingStartEffects(sessionId);
    ensureTabFocus(TabType.NOTE);
    const duration = freezeTimerAndSeedDuration();
    tutorial.onStop();
    updateSessionDuration({ sessionId, duration });
  }, [
    sessionId,
    ensureTabFocus,
    freezeTimerAndSeedDuration,
    tutorial,
    updateSessionDuration,
  ]);

  return useMemo(
    () => ({
      // Wrapped: the options object must never reach the `isPaused` argument.
      stopRecording: (options?: RecordingControlOptions) =>
        onStopButtonPressed(false, options?.tracking),
      startRecording: onStartButtonPressed,
      pauseRecording: (options?: RecordingControlOptions) =>
        runStop(true, options?.tracking),
      onInterrupt,
    }),
    [onStopButtonPressed, onStartButtonPressed, onInterrupt, runStop]
  );
};
